import React, { Fragment, FunctionComponent, useContext, useEffect, useState } from 'react';

import { Text } from '../../shared/Text';
import { useForm } from 'react-hook-form';
import { Input } from '../../shared/Input/Input';
import { InputHolder, InputHolderItem } from '../../shared/InputHolder';
import { isValidEmail, isValidPhoneNumber, isValidpostalCode } from '../../../utils/validations';
import { formItems, transformDataForAPI } from './form-items';
import { Select } from '../../shared/Select';
import { Button } from '../../shared/Button';
import { Radio } from '../../shared/Radio';
import { Grid } from '../../shared/Grid';
import { Column } from '../../shared/Column';
import { useOffices, useZipcode } from '../../../utils';
import { AutcompleteSearch } from '../../shared/AutocompleteSearch';
import { Textarea } from '../../shared/Textarea';
import { API } from '../../../services';
import { useHistory } from "react-router-dom";
import { ArrowLongDown, LoaderDots } from '../../../icons/components';
import { InputAppointmentForm } from '../../shared/Input';
import { ValidationModel } from '../../../models/ValidationModel';
import { FormContext } from '../../../store/surplus/SurPlusContext';

export const AppointmentForm: FunctionComponent = () => {
	const {
		register,
		handleSubmit,
		getValues,
		setValue,
		formState: {errors, isValid},
	} = useForm({mode: 'onBlur'});

	const history = useHistory();
	const {fetchZipcode, validZipcode} = useZipcode();
	const [appointmentSuccess, setAppointmentSuccess] = useState(false);
	const [appointmentErrors, setAppointmentErrors] = useState<ValidationModel | null>(null);
	const {officesFilteredBySearchterm, fetchOffice} = useOffices();
	const { consumer, setIsLoading, isLoading } = useContext(FormContext);

	const onSubmit = (data: any) => {
		setAppointmentErrors(null);
		setIsLoading(true);
		API.post('clientcommunication/', transformDataForAPI(data, consumer.utmSource))
			.then(() => setAppointmentSuccess(true))
			.finally(() => setIsLoading(false))
			.catch((error: ValidationModel) => {
				setAppointmentErrors(error);
				//console.table(error);
			});
	};

	const goToResult = () => {
		history.push('/')
	}

	const resetValuesZipcode = (zipcode: string) => {
		setValue('zipcode', zipcode, {shouldValidate: true});
		setValue('streetname', '');
		setValue('city', '');
	};

	const getCityAndPlace = () => {
		const values = getValues();
		const zipcode = values[formItems.zipcode] as string;
		const housenumber = values[formItems.housenumber];

		if (zipcode && housenumber) {

			fetchOffice(zipcode).then((res) => {
				if (res.length) {
					setValue('office', res[0].fullName);
				}
			})

			fetchZipcode(zipcode, housenumber)
				.then((res) => {
					const {streetname, city} = res;

					if (streetname && city) {
						setValue('streetname', streetname, {shouldValidate: true});
						setValue('city', city, {shouldValidate: true});
					} else {
						resetValuesZipcode(zipcode);
					}
				})
				.catch(() => {
					resetValuesZipcode(zipcode);
				});
		}
	};

	const filterOffices = (searchValue: string) => {
		const filteredItems = [...officesFilteredBySearchterm].filter((item) =>
			item.searchterm.toLowerCase().includes(searchValue.toLowerCase()),
		);

		return filteredItems;
	};

	return (
		<div className="c-stepper c-appointment">
			<div className="stepper__wrapper">
				<button className="stepper__back-button" onClick={goToResult}>
                    <span className="stepper__back-button-icon">
                        <ArrowLongDown width={12}/>
                    </span>
					Terug naar berekening
				</button>
				<div className="stepper__step stepper__step--is-active stepper__step--is-touched">
						<div className="stepper__step-content">
							{ appointmentSuccess ? (
							<div className="u-padding-bottom--sm">
								<Text as="h3">Bedankt voor het maken van een afspraak</Text>
								<p>
									De adviseur van De Hypotheker neemt zo spoedig mogelijk contact
									op om de afspraak in te plannen.
								</p>
							</div>
							) : (
							<Fragment>
								<Text as="h3">Vrijblijvend gesprek met Hypotheekadviseur</Text>
								<form onSubmit={handleSubmit(onSubmit)}>
								<div className="u-margin-bottom--md">
									<Select
										label="Reden voor afspraak"
										name={formItems.reason}
										id={formItems.reason}
										register={register}
										validation={{required: true}}
										value="woning op het oog"
										options={[
											{
												value: 'overwaarde benutten',
												name: 'overwaarde benutten',
											},
											{
												value: 'oriënteren',
												name: 'oriënteren',
											},
											{
												value: 'woning op het oog',
												name: 'woning op het oog',
											},
											{
												value: 'woning gekocht',
												name: 'woning gekocht',
											},
											{
												value: 'hypotheek oversluiten',
												name: 'hypotheek oversluiten',
											},
											{
												value: 'tweede hypotheek',
												name: 'tweede hypotheek',
											},
											{
												value: 'einde rentevaste periode',
												name: 'einde rentevaste periode',
											},
											{
												value: 'andere vraag',
												name: 'andere vraag',
											},
										]}
									/>
								</div>

								<InputHolder>
									<InputHolderItem isSmall={true}>
										<Radio
											label="Man"
											checked={true}
											name={formItems.gender}
											id={`${formItems.gender}-1`}
											defaultValue={'male'}
											register={register}
										/>
									</InputHolderItem>
									<InputHolderItem isSmall={true}>
										<Radio
											label="Vrouw"
											name={formItems.gender}
											id={`${formItems.gender}-2`}
											defaultValue={'female'}
											register={register}
										/>
									</InputHolderItem>
								</InputHolder>

								<div className="u-margin-top--md">
									<Grid>
										<Column cols={[6, null, null, 4]}>
											<Input
												label="Voorletters*"
												name={formItems.name}
												id={formItems.name}
												register={register}
												hasError={errors[formItems.name]}
												validation={{required: true}}
											/>
											{errors[formItems.name] && (
												<span className="form__error">Dit veld is verplicht</span>
											)}
										</Column>
										<Column cols={[6, null, null, 3]}>
											<Input
												label="Tussenvoegsel"
												name={formItems.addition}
												id={formItems.addition}
												register={register}
												hasError={errors[formItems.addition]}
											/>
										</Column>
										<Column cols={[12, null, null, 5]}>
											<InputAppointmentForm
												label="Achternaam*"
												name={formItems.lastname}
												id={formItems.lastname}
												register={register}
												hasError={errors[formItems.lastname]}
												validation={{required: true}}
											/>
											{errors[formItems.lastname] && (
												<span className="form__error">Dit veld is verplicht</span>
											)}
										</Column>
									</Grid>
								</div>

								<div className="u-margin-top--md">
									<Grid>
										<Column cols={[7, null, null, 9]}>
											<InputAppointmentForm
												label="Postcode*"
												name={formItems.zipcode}
												id={formItems.zipcode}
												register={register}
												hasError={errors[formItems.zipcode]}
												validation={{
													required: true,
													validate: validZipcode && isValidpostalCode,
													onBlur: getCityAndPlace,
												}}
											/>
											{errors[formItems.zipcode] && (
												<span className="form__error">Dit veld is verplicht</span>
											)}
										</Column>
										<Column cols={[5, null, null, 3]}>
											<InputAppointmentForm
												label="Huisnummer*"
												name={formItems.housenumber}
												id={formItems.housenumber}
												register={register}
												validation={{
													required: true,
													onBlur: getCityAndPlace,
												}}
												hasError={errors[formItems.housenumber]}
											/>
											{errors[formItems.housenumber] && (
												<span className="form__error">Dit veld is verplicht</span>
											)}
										</Column>
									</Grid>
								</div>

								<div className={`${validZipcode ? 'u-hidden' : ''}`}>
															<span className="u-text--info">
																	We hebben geen adres gevonden. Controleer de invoer of vul de
																	adresgegevens hieronder aan.
															</span>
									<InputHolder>
										<InputHolderItem>
											<InputAppointmentForm
												label="Straatnaam*"
												name={formItems.streetname}
												id={formItems.streetname}
												register={register}
												hasError={errors[formItems.streetname]}
												validation={{
													required: true,
												}}
											/>
											{errors[formItems.streetname] && (
												<span className="form__error">Dit veld is verplicht</span>
											)}
										</InputHolderItem>
										<InputHolderItem>
											<InputAppointmentForm
												label="Plaatsnaam*"
												name={formItems.city}
												id={formItems.city}
												register={register}
												hasError={errors[formItems.city]}
												validation={{
													required: true,
												}}
											/>
											{errors[formItems.city] && (
												<span className="form__error">Dit veld is verplicht</span>
											)}
										</InputHolderItem>
									</InputHolder>
								</div>


								<div className="u-margin-top--md">
									<InputAppointmentForm
										label="Telefoonnummer*"
										name={formItems.phoneNumber}
										id={formItems.phoneNumber}
										register={register}
										hasError={errors[formItems.phoneNumber]}
										type="tel"
										helperLabel="Wij bellen je op dit nummer om de afspraak te maken"
										validation={{required: true, validate: isValidPhoneNumber}}
									/>
									{errors[formItems.phoneNumber] && (
										<span className="form__error">
																			Vul een tien-cijferig telefoonnummer in (voorbeeld: 06-12345678 of
																			010-1234567)
																	</span>
									)}
								</div>

								<div className="u-margin-top--md">
									<InputAppointmentForm
										label="E-mailadres"
										name={formItems.email}
										id={formItems.email}
										register={register}
										hasError={errors[formItems.email]}
										type="email*"
										helperLabel="Je ontvangt per e-mail een bevestiging en aanvullende informatie"
										validation={{required: true, validate: isValidEmail}}
									/>
									{errors[formItems.email] && (
										<span className="form__error">
																			Vul een correct e-mailadres in (bijv. user@domain.com)
																	</span>
									)}
								</div>
{}
								{officesFilteredBySearchterm.length > 0 && (
									<div className="u-margin-top--md">
										<AutcompleteSearch
											input={{
												type: 'autocomplete',
												label: 'Vestiging*',
												helperLabel: 'Kies de vestiging waar je een afspraak wil maken',
												placeholder: 'Kies een vestiging',
												name: formItems.office,
												id: formItems.office,
												register: register,
												hasError: errors[formItems.office],
												validation: {required: true},
											}}
											setValue={(value) => {
												setValue('office', value, {shouldValidate: true});
											}}
											filterCallback={filterOffices}
											items={officesFilteredBySearchterm}
										/>
										{errors[formItems.office] && (
											<span className="form__error">Dit veld is verplicht</span>
										)}
									</div>
								)}

								<div className="u-margin-top--md">
									<Textarea
										label="Eventuele opmerkingen"
										name={formItems.remarks}
										id={formItems.remarks}
										register={register}
										hasError={errors[formItems.remarks]}
										placeholder="Korte omschrijving hier"
									/>
								</div>
									{appointmentErrors?.errors && (
										Object.values(appointmentErrors.errors).map((error, index) => (
											<div key={index} className="u-margin-top--md">
												<span className="form__error">{error}</span>
											</div>
										))
									)}
								<div className="u-padding-top--md u-padding-bottom--md">
									<Button label="Maak een afspraak" disabled={!isValid || isLoading}/>
								</div>
							</form>
							</Fragment>
							)}
						</div>
					</div>
				</div>
		</div>
	);
};
